import { Fragment, ReactNode } from 'react';

import { useIsSecuritySunsetEnabled } from 'features/organization/security-insights/hooks/useIsSecuritySunsetEnabled';

import {
  AgentAutoscalerComplete,
  AgentCostComplete,
  AgentGpuMetricsComplete,
  AgentNetworkCostComplete,
  AgentOmniComplete,
  AgentSecurityComplete,
  AgentWorkloadAutoscalerComplete,
  AgentNvidiaDevicePluginComplete,
} from 'hooks/dialogs/cluster/components/features';
import { EXTRA_FEATURES, MAIN_FEATURES } from 'hooks/dialogs/cluster/constants';
import { FeatureName, FeatureSelection } from '../types';

import { Typography, Stack, colorOrToken, tokens } from '@cast/design-system';

type Props = {
  enabledFeatures: FeatureSelection;
};

export function AssignCredentialsFeaturesComplete({ enabledFeatures }: Props) {
  const isSecuritySunsetEnabled = useIsSecuritySunsetEnabled();

  const featureComponentMap: Record<
    FeatureName,
    (props: { checked?: boolean }) => ReactNode
  > = {
    costMonitoring: AgentCostComplete,
    autoscaler: AgentAutoscalerComplete,
    workloadAutoscaler: AgentWorkloadAutoscalerComplete,
    security: isSecuritySunsetEnabled ? () => null : AgentSecurityComplete,
    omni: AgentOmniComplete,
    networkCostMetrics: AgentNetworkCostComplete,
    gpuMetricsExporter: AgentGpuMetricsComplete,
    nvidiaGpuPlugin: AgentNvidiaDevicePluginComplete,
    reliabilityMetrics: () => null,
    kent: () => null,
  };

  const mainFeatures = MAIN_FEATURES.map((feature) => ({
    feature,
    Component: featureComponentMap[feature],
  }));

  const extraFeatures = EXTRA_FEATURES.map((feature) => ({
    feature,
    Component: featureComponentMap[feature],
  }));

  return (
    <Stack data-testid="features-container">
      {mainFeatures.map(({ feature, Component }) => (
        <Fragment key={feature}>
          <Component checked={enabledFeatures[feature]} />
        </Fragment>
      ))}
      <Stack ml={6} mr={80}>
        <Typography
          variant="L10M"
          color={colorOrToken('grey.400', tokens.theme.fg.structural.secondary)}
          mt={12}
          mb={12}
        >
          Extra components
        </Typography>
        {extraFeatures.map(({ feature, Component }) => (
          <Fragment key={feature}>
            <Component checked={enabledFeatures[feature]} />
          </Fragment>
        ))}
      </Stack>
    </Stack>
  );
}
